import { createContext, useContext, useState, useCallback, useMemo } from "react";

interface AutoSaveState {
	error: string | null;
	isSaving: boolean;
	lastSavedAt: Date | null;
}

interface AutoSaveContextValue extends AutoSaveState {
	setError: (error: string | null) => void;
	setLastSavedAt: (date: Date) => void;
	setSaving: (saving: boolean) => void;
}

const AutoSaveContext = createContext<AutoSaveContextValue | null>(null);

const AutoSaveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [state, setState] = useState<AutoSaveState>({
		isSaving: false,
		lastSavedAt: null,
		error: null,
	});

	const setSaving = useCallback((saving: boolean) => {
		setState((prev) => ({ ...prev, isSaving: saving }));
	}, []);

	const setLastSavedAt = useCallback((date: Date) => {
		setState((prev) => ({ ...prev, lastSavedAt: date, error: null }));
	}, []);

	const setError = useCallback((error: string | null) => {
		setState((prev) => ({ ...prev, error }));
	}, []);

	const value = useMemo(
		() => ({ ...state, setSaving, setLastSavedAt, setError }),
		[state, setSaving, setLastSavedAt, setError],
	);

	return (
		<AutoSaveContext.Provider value={value}>
			{children}
		</AutoSaveContext.Provider>
	);
};

const useAutoSaveContext = (): AutoSaveContextValue | null => {
	return useContext(AutoSaveContext);
};

export { AutoSaveProvider, useAutoSaveContext };
